/**
 * Multipart Search Hook
 *
 * Provides functionality for searching multiple parts with shared vehicle context.
 * Features:
 * - Search multiple parts in parallel
 * - Results grouped by source then by part
 * - Part normalization and deduplication info
 * - Partial failure handling
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { getGatewayUrl } from '../utils/api';
import { getApiKeyHeadersAsync } from '../utils/auth';

// Types
export interface PartQuery {
  partDescription?: string;
  subAggregate?: string;
  partNumber?: string;
  brand?: string;
}

export interface VehicleContext {
  make?: string;
  model?: string;
  variant?: string;
  fuelType?: string;
  year?: number;
}

export interface PartNormalizationInfo {
  originalInput: string;
  canonicalForm: string;
  mergedWith?: string;
  corrected: boolean;
  confidence: number;
}

export interface PartSummary {
  partKey: string;
  originalInput?: string;
  totalResults: number;
  found: boolean;
  message?: string;
  sourceBreakdown: Record<string, number>;
}

export interface PartsNormalizationSummary {
  originalCount: number;
  uniqueCount: number;
  normalized: PartNormalizationInfo[];
}

export interface PartialFailure {
  partKey: string;
  source?: string;
  error: string;
  errorCode?: string;
}

export interface MultipartSearchData {
  results: Record<string, Record<string, any[]>>;
  partsSummary: PartSummary[];
  partsNormalization?: PartsNormalizationSummary;
  vehicle?: VehicleContext;
  batchId: string;
  totalParts: number;
  totalResults: number;
  responseTime: number;
  partialFailures: PartialFailure[];
}

export interface MultipartSearchResponse {
  success: boolean;
  data: MultipartSearchData;
  timestamp: string;
}

export interface MultipartSearchRequest {
  query?: string;
  parts?: PartQuery[];
  vehicle?: VehicleContext;
  sources?: ('tvs' | 'boodmo' | 'smart')[];
  limitPerPart?: number;
  correlationId?: string;  // Pass correlation_id from validation to link log entries
}

interface UseMultipartSearchState {
  isLoading: boolean;
  error: string | null;
  data: MultipartSearchData | null;
}

interface UseMultipartSearchReturn extends UseMultipartSearchState {
  search: (request: MultipartSearchRequest) => Promise<MultipartSearchResponse | null>;
  searchByQuery: (query: string, vehicle?: VehicleContext) => Promise<MultipartSearchResponse | null>;
  searchByParts: (parts: PartQuery[], vehicle?: VehicleContext) => Promise<MultipartSearchResponse | null>;
  reset: () => void;
}

/**
 * Hook for multipart search functionality
 */
export function useMultipartSearch(): UseMultipartSearchReturn {
  const [state, setState] = useState<UseMultipartSearchState>({
    isLoading: false,
    error: null,
    data: null,
  });

  const mountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Main search function
   */
  const search = useCallback(
    async (request: MultipartSearchRequest): Promise<MultipartSearchResponse | null> => {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      if (mountedRef.current) {
        setState({ isLoading: true, error: null, data: null });
      }

      try {
        const baseUrl = getGatewayUrl();
        const headers = await getApiKeyHeadersAsync();

        const response = await fetch(`${baseUrl}/api/search/multipart`, {
          method: 'POST',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
            // Pass correlation_id from validation to link log entries
            ...(request.correlationId ? { 'X-Correlation-Id': request.correlationId } : {}),
          },
          body: JSON.stringify({
            query: request.query,
            parts: request.parts,
            vehicle: request.vehicle,
            sources: request.sources || ['tvs', 'boodmo'],
            limitPerPart: request.limitPerPart || 10,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Search failed: ${response.status}`);
        }

        const result: MultipartSearchResponse = await response.json();

        if (mountedRef.current) {
          setState({
            isLoading: false,
            error: result.success ? null : 'Search returned unsuccessful',
            data: result.data,
          });
        }

        return result;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // Request was cancelled, don't update state
          return null;
        }

        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('Multipart search error:', error);

        if (mountedRef.current) {
          setState({
            isLoading: false,
            error: errorMessage,
            data: null,
          });
        }

        return null;
      }
    },
    []
  );

  /**
   * Search by natural language query
   */
  const searchByQuery = useCallback(
    async (
      query: string,
      vehicle?: VehicleContext
    ): Promise<MultipartSearchResponse | null> => {
      return search({ query, vehicle });
    },
    [search]
  );

  /**
   * Search by explicit parts list
   */
  const searchByParts = useCallback(
    async (
      parts: PartQuery[],
      vehicle?: VehicleContext
    ): Promise<MultipartSearchResponse | null> => {
      return search({ parts, vehicle });
    },
    [search]
  );

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setState({
      isLoading: false,
      error: null,
      data: null,
    });
  }, []);

  return {
    ...state,
    search,
    searchByQuery,
    searchByParts,
    reset,
  };
}

/**
 * Fetch available parts for dropdown
 */
export async function fetchAvailableParts(
  source: string = 'tvs',
  searchTerm?: string,
  limit: number = 50
): Promise<string[]> {
  try {
    const baseUrl = getGatewayUrl();
    const headers = await getApiKeyHeadersAsync();

    const params = new URLSearchParams({
      source,
      limit: limit.toString(),
    });

    if (searchTerm) {
      params.append('search', searchTerm);
    }

    const response = await fetch(
      `${baseUrl}/api/search/multipart/parts?${params.toString()}`,
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch parts: ${response.status}`);
    }

    const result = await response.json();
    return result.data?.parts || [];
  } catch (error) {
    console.error('Error fetching available parts:', error);
    return [];
  }
}

// Parallel source search types
export interface SourceStatus {
  source: string;
  status: 'pending' | 'loading' | 'completed' | 'error' | 'skipped';
  results?: Record<string, any[]>;
  totalResults?: number;
  responseTime?: number;
  error?: string;
  partsSearched?: string[];  // Which parts were searched in this source
  partsFound?: string[];     // Which parts had results in this source
}

export interface UseMultipartSearchParallelState {
  isLoading: boolean;
  error: string | null;
  data: MultipartSearchData | null;
  sourceStatuses: SourceStatus[];
}

interface UseMultipartSearchParallelReturn extends UseMultipartSearchParallelState {
  searchParallel: (request: MultipartSearchRequest) => void;
  searchByQuery: (query: string, vehicle?: VehicleContext, correlationId?: string) => void;
  searchByParts: (parts: PartQuery[], vehicle?: VehicleContext, correlationId?: string) => void;
  reset: () => void;
}

/**
 * Hook for parallel multipart search - calls each source separately
 * Shows results progressively as each source completes
 */
export function useMultipartSearchParallel(): UseMultipartSearchParallelReturn {
  const [state, setState] = useState<UseMultipartSearchParallelState>({
    isLoading: false,
    error: null,
    data: null,
    sourceStatuses: [],
  });

  const mountedRef = useRef(true);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortControllersRef.current.forEach(controller => controller.abort());
    };
  }, []);

  /**
   * Search a single source
   */
  const searchSource = useCallback(
    async (
      source: string,
      request: MultipartSearchRequest,
      headers: HeadersInit
    ): Promise<{ source: string; data: MultipartSearchData | null; error: string | null; responseTime: number }> => {
      const startTime = Date.now();
      const controller = new AbortController();
      abortControllersRef.current.set(source, controller);

      try {
        const baseUrl = getGatewayUrl();
        const response = await fetch(`${baseUrl}/api/search/multipart`, {
          method: 'POST',
          headers: {
            ...(headers as Record<string, string>),
            'Content-Type': 'application/json',
            // Pass correlation_id from validation to link log entries
            ...(request.correlationId ? { 'X-Correlation-Id': request.correlationId } : {}),
          },
          body: JSON.stringify({
            query: request.query,
            parts: request.parts,
            vehicle: request.vehicle,
            sources: [source],
            limitPerPart: request.limitPerPart || 10,
          }),
          signal: controller.signal,
        });

        const responseTime = Date.now() - startTime;

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          return {
            source,
            data: null,
            error: errorData.detail || `Search failed: ${response.status}`,
            responseTime,
          };
        }

        const result: MultipartSearchResponse = await response.json();
        return {
          source,
          data: result.data,
          error: result.success ? null : 'Search returned unsuccessful',
          responseTime,
        };
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return { source, data: null, error: null, responseTime: 0 };
        }
        return {
          source,
          data: null,
          error: error instanceof Error ? error.message : 'Unknown error',
          responseTime: Date.now() - startTime,
        };
      }
    },
    []
  );

  /**
   * Main search function - Sequential with per-part fallback
   * 1. Search TVS first for ALL parts
   * 2. Only search Boodmo for specific parts NOT found in TVS
   * 3. Only search Smart for specific parts NOT found in TVS or Boodmo
   */
  const searchParallel = useCallback(
    (request: MultipartSearchRequest) => {
      // Abort any pending requests
      abortControllersRef.current.forEach(controller => controller.abort());
      abortControllersRef.current.clear();

      const sources = request.sources || ['tvs', 'boodmo'];
      const startTime = Date.now();

      // Get all part keys from the request for tracking
      const allPartKeys = request.parts?.map(p =>
        p.partDescription || p.subAggregate || p.partNumber || ''
      ).filter(Boolean) || [];

      // Initialize state - first source loading, others pending
      const initialStatuses: SourceStatus[] = sources.map((source, idx) => ({
        source,
        status: idx === 0 ? ('loading' as const) : ('pending' as const),
        partsSearched: idx === 0 ? allPartKeys : undefined,
      }));

      if (mountedRef.current) {
        setState({
          isLoading: true,
          error: null,
          data: null,
          sourceStatuses: initialStatuses,
        });
      }

      // Get headers and start sequential search with per-part fallback
      getApiKeyHeadersAsync().then(async headers => {
        const allResults: Record<string, Record<string, any[]>> = {};
        let partsSummary: PartSummary[] = [];
        let totalResults = 0;
        const partsWithResults = new Set<string>();
        let currentRequest = request;

        // Process sources sequentially: TVS -> Boodmo -> Smart
        for (let i = 0; i < sources.length; i++) {
          const source = sources[i];
          if (!mountedRef.current) break;

          // Get which parts we're searching in this source
          const partsBeingSearched = currentRequest.parts?.map(p =>
            p.partDescription || p.subAggregate || p.partNumber || ''
          ).filter(Boolean) || [];

          // Skip this source if no parts to search
          if (i > 0 && partsBeingSearched.length === 0) {
            setState(prev => ({
              ...prev,
              sourceStatuses: prev.sourceStatuses.map(s =>
                s.source === source ? {
                  ...s,
                  status: 'skipped' as const,
                  totalResults: 0,
                  partsSearched: [],
                  partsFound: [],
                } : s
              ),
            }));
            continue;
          }

          // Update status to loading for this source with parts being searched
          setState(prev => ({
            ...prev,
            sourceStatuses: prev.sourceStatuses.map(s =>
              s.source === source ? {
                ...s,
                status: 'loading' as const,
                partsSearched: partsBeingSearched,
              } : s
            ),
          }));

          // Search this source with current request (may have filtered parts)
          const result = await searchSource(source, currentRequest, headers);
          if (!mountedRef.current) break;

          // Get parts that have results in this source
          const sourceResults = result.data?.results?.[source] || {};
          let sourceTotal = 0;
          const partsFoundInSource: string[] = [];

          Object.entries(sourceResults).forEach(([partKey, results]) => {
            if (Array.isArray(results) && results.length > 0) {
              partsFoundInSource.push(partKey);
              partsWithResults.add(partKey);
              sourceTotal += results.length;
            }
          });

          // Store results
          if (sourceTotal > 0) {
            allResults[source] = sourceResults;
            totalResults += sourceTotal;
          }

          // Update parts summary from first source response
          if (result.data?.partsSummary && result.data.partsSummary.length > 0 && i === 0) {
            partsSummary = result.data.partsSummary;
          }

          // Find parts NOT found in this source for next source
          const partsNotFound = partsSummary.filter(p => !partsWithResults.has(p.partKey));
          const allPartsFound = partsNotFound.length === 0 && partsSummary.length > 0;

          // Create request for next source with ONLY missing parts
          if (!allPartsFound && i < sources.length - 1) {
            const missingPartQueries = partsNotFound.map(p => ({
              partDescription: p.originalInput || p.partKey,
            }));
            currentRequest = {
              ...request,
              parts: missingPartQueries,
              query: undefined, // Use explicit parts, not query
            };
          }

          // Update state
          const isLastSource = i === sources.length - 1;
          const skipRemainingSources = allPartsFound && !isLastSource;

          setState(prev => {
            const newSourceStatuses = prev.sourceStatuses.map(s => {
              if (s.source === source) {
                return {
                  ...s,
                  status: result.error ? ('error' as const) : ('completed' as const),
                  results: sourceResults,
                  totalResults: sourceTotal,
                  responseTime: result.responseTime,
                  error: result.error || undefined,
                  partsSearched: partsBeingSearched,
                  partsFound: partsFoundInSource,
                };
              }
              // Mark remaining sources as skipped if all parts found
              if (skipRemainingSources && s.status === 'pending') {
                return {
                  ...s,
                  status: 'skipped' as const,
                  totalResults: 0,
                  responseTime: 0,
                  partsSearched: [],
                  partsFound: [],
                };
              }
              return s;
            });

            const allDone = newSourceStatuses.every(
              s => s.status === 'completed' || s.status === 'error' || s.status === 'skipped'
            );

            return {
              ...prev,
              isLoading: !allDone && !skipRemainingSources,
              sourceStatuses: newSourceStatuses,
              data: {
                results: { ...allResults },
                partsSummary,
                batchId: result.data?.batchId || prev.data?.batchId || '',
                totalParts: result.data?.totalParts || prev.data?.totalParts || partsSummary.length,
                totalResults,
                responseTime: Date.now() - startTime,
                partialFailures: [],
                vehicle: result.data?.vehicle || prev.data?.vehicle,
              },
            };
          });

          // Skip remaining sources if all parts found
          if (allPartsFound) {
            console.log(`[Search] All ${partsSummary.length} parts found in ${source}, skipping remaining sources`);
            break;
          } else if (partsNotFound.length > 0 && i < sources.length - 1) {
            console.log(`[Search] ${partsNotFound.length} parts not found in ${source}, searching next source for: ${partsNotFound.map(p => p.partKey).join(', ')}`);
          }
        }
      }).catch(error => {
        console.error('Sequential search error:', error);
        if (mountedRef.current) {
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Search failed',
          }));
        }
      });
    },
    [searchSource]
  );

  /**
   * Search by natural language query (parallel)
   */
  const searchByQuery = useCallback(
    (query: string, vehicle?: VehicleContext, correlationId?: string) => {
      searchParallel({ query, vehicle, correlationId });
    },
    [searchParallel]
  );

  /**
   * Search by explicit parts list (parallel)
   */
  const searchByParts = useCallback(
    (parts: PartQuery[], vehicle?: VehicleContext, correlationId?: string) => {
      searchParallel({ parts, vehicle, correlationId });
    },
    [searchParallel]
  );

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current.clear();
    setState({
      isLoading: false,
      error: null,
      data: null,
      sourceStatuses: [],
    });
  }, []);

  return {
    ...state,
    searchParallel,
    searchByQuery,
    searchByParts,
    reset,
  };
}

export default useMultipartSearch;
